import { forwardRef, useEffect, useRef, type ReactNode } from 'react';
import { ListChecks, ScanSearch, Share2, ShieldCheck, Sparkles, TrendingUp } from 'lucide-react';
import { SymphonyMark } from '../../SymphonyMark';
import { Button, Card, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useLocale } from '../../../lib/locale-context';
import { useProductName } from '../../../lib/use-brand';
import { winFigure, winFigureParts, type FigureCopy, type WebWin, type WinFigure, type WinKind } from '../../../lib/wins';
import { SETTLE_MS, TEAR_EASING, TEAR_MS, tearClipPaths, tearPoses } from '../../../lib/win-tear';
import styles from './WinCard.module.css';

const KIND_ICON: Record<WinKind, ReactNode> = {
  CAUGHT: <ScanSearch size={16} aria-hidden />,
  PREVENTED: <ShieldCheck size={16} aria-hidden />,
  IMPROVED: <TrendingUp size={16} aria-hidden />,
  HANDLED: <ListChecks size={16} aria-hidden />,
};

const USER_ATTRIBUTIONS = new Set(['you', 'user', 'the user', 'owner', 'the owner']);

export type WinCardLayout = 'grid' | 'reel' | 'preview';

export function useFigureCopy(): FigureCopy {
  const { t } = useTranslation();
  return {
    minutes: (amount) => t('wins.figure.minutes', { amount }),
    hours: (amount) => t('wins.figure.hours', { amount }),
    days: (amount) => t('wins.figure.days', { amount }),
    timeLabel: t('wins.figure.timeLabel'),
    moneyLabel: t('wins.figure.moneyLabel'),
    atStakeLabel: t('wins.figure.atStakeLabel'),
  };
}

export function useWinFigure(win: WebWin): WinFigure | null {
  const copy = useFigureCopy();
  const { locale } = useLocale();
  return winFigure(win, copy, locale);
}

export function formatWinDate(iso: string, locale: string): string {
  const d = new Date(iso);
  try {
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  } catch {
    return d.toLocaleDateString();
  }
}

/** The shell every Wins card shares: the pastel surface, the Symphony footer and the Share action. */
export const WinSurface = forwardRef<HTMLElement, {
  tone: string;
  layout: WinCardLayout;
  showShare: boolean;
  onShare?: () => void;
  shareLabel: string;
  children: ReactNode;
  className?: string;
}>(function WinSurface({ tone, layout, showShare, onShare, shareLabel, children, className }, ref) {
  const productName = useProductName();
  return (
    // The DS Card skins carry no pastel, and the pastel is the design: it says which kind of win this is.
    // So the card keeps the DS padding, radius and border, and only the fill comes from the tone.
    <Card ref={ref} component="article" className={[styles.card, styles[layout], className].filter(Boolean).join(' ')} data-tone={tone}>
      {children}
      <div className={styles.foot}>
        <span className={styles.brand}>
          <SymphonyMark size={16} />
          <span>{productName}</span>
        </span>
        {layout !== 'preview' && onShare && (
          <span className={styles.actions} data-hidden={showShare ? undefined : ''}>
            <Button size="small" startIcon={<Share2 size={16} aria-hidden />} onClick={onShare}>
              {shareLabel}
            </Button>
          </span>
        )}
      </div>
    </Card>
  );
});

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const TORN_SHADOW = 'drop-shadow(0 8px 8px rgba(var(--ds-black-rgb), 0.18))';

/**
 * Tears the ticket along its perforation, then hands over to `done`. The two halves are separate
 * elements, so each animates on its own; for the tear only, the card stops clipping its corners and the
 * halves carry them instead, or the lifted pieces would be cut off at the card's edge.
 */
function tearTicket(card: HTMLElement, main: HTMLElement, stub: HTMLElement, done: () => void): () => void {
  const rtl = getComputedStyle(card).direction === 'rtl';
  const clips = tearClipPaths(card.offsetWidth);
  const poses = tearPoses(rtl);
  const radius = getComputedStyle(card).borderTopLeftRadius;
  card.style.overflow = 'visible';
  main.style.clipPath = clips.main;
  stub.style.clipPath = clips.stub;
  main.style.borderStartStartRadius = main.style.borderStartEndRadius = radius;
  stub.style.borderEndStartRadius = stub.style.borderEndEndRadius = radius;
  main.style.transformOrigin = rtl ? 'bottom right' : 'bottom left';
  stub.style.transformOrigin = rtl ? 'top left' : 'top right';

  const tear = (pose: string) => [
    { transform: 'none', filter: 'none', offset: 0 },
    { transform: 'translateY(-2px)', offset: 0.18 },
    { transform: pose, filter: TORN_SHADOW, offset: 0.76 },
    { transform: pose, filter: TORN_SHADOW, offset: 1 },
  ];
  const settle = (pose: string) => [
    { transform: pose, filter: TORN_SHADOW },
    { transform: 'none', filter: 'none' },
  ];
  const running: Animation[] = [];
  let cancelled = false;
  const reset = () => {
    for (const a of running) a.cancel();
    card.style.overflow = '';
    for (const el of [main, stub]) el.removeAttribute('style');
  };
  const play = (el: HTMLElement, frames: Keyframe[], duration: number) => {
    const a = el.animate(frames, { duration, easing: TEAR_EASING, fill: 'forwards' });
    running.push(a);
    return a.finished;
  };

  Promise.all([play(main, tear(poses.main), TEAR_MS), play(stub, tear(poses.stub), TEAR_MS)])
    .then(() => {
      if (cancelled) return;
      done();
      return Promise.all([play(main, settle(poses.main), SETTLE_MS), play(stub, settle(poses.stub), SETTLE_MS)]);
    })
    .catch(() => undefined)
    .finally(() => {
      if (!cancelled) reset();
    });
  return () => {
    cancelled = true;
    reset();
  };
}

export function WinCard({
  win,
  layout,
  showShare = true,
  onShare,
}: {
  win: WebWin;
  layout: WinCardLayout;
  showShare?: boolean;
  /** Called with where the card sat on the page, so the share dialog can fly its preview in from there. */
  onShare?: (from?: DOMRect) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const productName = useProductName();
  const figure = useWinFigure(win);
  const parts = figure ? winFigureParts(figure.label) : { lead: '', tail: '' };
  const kindLine = [win.kind ? t(`wins.kind.${win.kind}`) : '', win.domain ? t(`wins.domain.${win.domain}`) : ''].filter(Boolean).join(' · ');
  const attribution = win.quote?.attribution;
  const quoteBy = !attribution || USER_ATTRIBUTIONS.has(attribution.toLowerCase()) ? t('wins.card.you') : attribution;

  const cardRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const stubRef = useRef<HTMLDivElement>(null);
  const stopTear = useRef<(() => void) | null>(null);
  useEffect(() => () => stopTear.current?.(), []);

  const share = () => {
    if (!onShare || stopTear.current) return;
    const card = cardRef.current;
    const from = card?.getBoundingClientRect();
    const main = mainRef.current;
    const stub = stubRef.current;
    if (!card || !main || !stub || typeof main.animate !== 'function' || prefersReducedMotion()) {
      onShare(from);
      return;
    }
    stopTear.current = tearTicket(card, main, stub, () => {
      stopTear.current = null;
      onShare(from);
    });
  };

  return (
    <Card
      ref={cardRef}
      component="article"
      disablePadding
      className={[styles.ticket, styles[layout]].join(' ')}
      data-tone={win.kind ?? 'HANDLED'}
    >
      <div ref={mainRef} className={styles.main}>
        <div className={`sym-type-tiny-regular ${styles.meta}`}>
          <span className={styles.kind}>
            {win.kind ? KIND_ICON[win.kind] : <Sparkles size={16} aria-hidden />}
            {kindLine}
          </span>
          <span className={styles.when}>{formatWinDate(win.createdDate, locale)}</span>
        </div>
        <h3 className={styles.headline} dir="auto">
          {win.title}
        </h3>
        {figure && (
          <p className={styles.figLine} dir="auto">
            <bdi className={styles.fig}>
              {figure.estimated && <span title={t('wins.figure.estimated')}>~</span>}
              {figure.value}
            </bdi>{' '}
            <span className={`sym-type-tiny-regular ${styles.lead}`}>{parts.lead}</span>
          </p>
        )}
        {win.description && (
          <Typography variant="p2" color="inherit" className={styles.story} dir="auto">
            {win.description}
          </Typography>
        )}
        {win.quote && (
          <p className={styles.quote} dir="auto">
            &ldquo;{win.quote.text}&rdquo; <span>— {quoteBy}</span>
          </p>
        )}
      </div>
      <div ref={stubRef} className={styles.stub}>
        {win.gain && (
          <span className={`sym-type-tiny-semi-bold ${styles.stamp}`} dir="auto">
            {win.gain}
          </span>
        )}
        {parts.tail && (
          <p className={`sym-type-tiny-regular ${styles.tail}`} dir="auto">
            {parts.tail}
          </p>
        )}
        <div className={styles.foot}>
          <span className={styles.brand}>
            <SymphonyMark size={16} />
            <span>{productName}</span>
          </span>
          {layout !== 'preview' && onShare && (
            <span className={styles.actions} data-hidden={showShare ? undefined : ''}>
              <Button size="small" startIcon={<Share2 size={16} aria-hidden />} onClick={share}>
                {t('wins.card.share')}
              </Button>
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
